import { type Dictionary, type EntityState } from '@ngrx/entity';

import {
  type CanvasElement,
  type CanvasElementFrom,
  type CanvasElementId,
  type CanvasElementType,
  type ColumnId,
  type ElementTargetPath,
  findByRefIds,
  type PartialPropsOf,
  type PropsOf,
  type SlotIdOf,
  type SlotsFor,
  WrapperField,
  type WrapperRefIds,
  type WrapperRefs,
} from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';
import { type ColumnDataType } from '@blaze-shared/api-contracts-domain';
import { getFormatByDataType } from '@blaze-shared/elements-domain';
import {
  type CanvasElementDataProperties,
  type DataBindingSourceInfoColumn,
  type DataBindingSourceType,
  findDisplayColumnOrGetSingle,
  findIdColumnKey,
  type FormContainerModelField,
  type FormListModelField,
  getOptionsListBindingForTagColumn,
  isFormListModelField,
  type PregenerationType,
} from '@blaze-shared/elements-domain';
import { GET_TIMEZONES_API_ID } from '@blaze-shared/platform-data-access';
import {
  DATE_DEFAULT_VALUE,
  DATETIME_DEFAULT_VALUE,
  findColumn,
  findColumnKey,
  getColumnKey,
  getLinkedInlineColumns,
  isLinkedInlineColumn,
  matchCustomColumn,
} from '@blaze-shared/schema-domain';
import {
  type BlazeTableSchema,
  type ColumnType,
  type DatasetColumn,
} from '@blaze-shared/schema-domain';
import { deepClone, toResolvedColumnName } from '@blaze-shared/shared-util';
import { type StyleSheet } from '@blaze-shared/theming-domain';
import { type FormActionType, getValueByPath, setValueByPath } from '@blaze-shared/workflow-domain';

import { type CanvasElementDefinitionInternal } from '../../canvas-elements/element-definition';
import { hasProps, hasWrapperRefs } from '../../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../../canvas-elements/element-model/element-type-guards';
import {
  type GenerationElementType,
  type GeneratorInputOption,
  getFormFieldsDataTypeMapping,
} from '../../canvas-elements/element-model/form-fields-generation';
import { updateGeneratedTextInputContent } from '../../canvas-elements/element-model/generated-field-props';
import { getFormInputNameByType } from '../../canvas-elements/element-model/io';
import { type PregenerationData } from '../../canvas-elements/element-model/pregeneration';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { type FormFieldGenerationParams } from '../../elements/__base/generation';
import { type DatetimeInputPropsModel } from '../../elements/datetime-input/datetime-input.props-model';
import { getElementPath } from '../canvas-element/path';
import { isElementChildOf } from '../layout/relationship';
import { removeElementMutablyWithoutSanitization } from '../layout/remove-element';

const ELEMENTS_HIDDEN_BY_DEFAULT_ON_INSERT_FORM: ColumnType[] = [
  'AutoNumber',
  'Formula',
  'CreatedBy',
  'CreatedTime',
  'LastUpdatedTime',
  'UpdatedBy',
];

export const COLUMN_CONTAINER_REFID = 'columns';
export const COMMIT_BUTTON_REFID = 'commit';

export interface FindFieldTargetPathData<TElementType> {
  element: TElementType;
  totalFields: number;
  isInitialGeneration: boolean;
  fieldIndex: number;
  isColumnsReflow?: boolean;
}

export type InitializeGeneratedChildPropsFn = <T extends CanvasElementType>(
  type: T,
  params: FormFieldGenerationParams<T>,
  styleSheets: StyleSheet[],
) => PartialPropsOf<T> | undefined;

export function pregenerationTypeToAction(pregenerationType?: PregenerationType): FormActionType {
  switch (pregenerationType) {
    case 'add':
      return 'insert';
    case 'edit':
      return 'update';
    case 'view':
      return 'view';
    case 'custom':
      return 'custom';
    case 'prompt':
      return 'prompt';
    // todo [ls] handle predefined when functionality available
    default:
      return 'custom';
  }
}

export interface CreateCanvasElementModel {
  inputData: PregenerationData;
  refIds: WrapperRefIds;
  elementDefinition: CanvasElementDefinitionInternal;
  atPath?: ElementTargetPath;
  initProps?: Partial<PropsOf<any>>;
  updateProps?: Partial<PropsOf<any>>;
}

export type CreateCanvasActionButtonModel = Pick<
  CreateCanvasElementModel,
  'refIds' | 'updateProps'
>;

export function cleanRemovedFieldsMutably(
  entityState: EntityState<CanvasElement>,
  pregenerationData: PregenerationData[],
  wrapperRefs: WrapperRefs,
): string[] {
  // Gather all included field refs
  const pregenerationRefIds = Object.values(pregenerationData)
    .filter((field) => field.included)
    .flatMap((field) => field.refIds);

  return cleanRemovedWrapperElementsMutably(
    entityState,
    pregenerationRefIds,
    wrapperRefs,
    WrapperField.isRefId,
  );
}

export function cleanRemovedWrapperElementsMutably(
  entityState: EntityState<CanvasElement>,
  pregenerationRefIds: string[],
  wrapperRefs: WrapperRefs,
  wrapperElementPredicate: (refId: string) => boolean,
): string[] {
  const removedElementIds: string[] = [];
  // Remove field elements which are no longer included
  Object.entries(wrapperRefs).forEach(([refId, refElementId]) => {
    if (wrapperElementPredicate(refId) && !pregenerationRefIds.some((rId) => rId === refId)) {
      delete wrapperRefs[refId];
      // This will not properly sanitize the elements
      // But to do this the form and editable form generation logic would need to be moved to the reducer
      removeElementMutablyWithoutSanitization(entityState, refElementId);
      removedElementIds.push(refElementId);
    }
  });
  return removedElementIds;
}

/**
 * Populates missing control types in the model fields.
 * Updates container's model.columns `visible` flag
 * Used during initial setup.
 */
export function updateGenerationAndModelMismatchesMutably(
  model: (FormContainerModelField | FormListModelField)[] | undefined,
  pregenerationData: PregenerationData[],
): void {
  if (!model) {
    return;
  }

  for (const inputData of pregenerationData) {
    const modelItem = model.find(matchCustomColumn(inputData, 'fieldId', 'field'));
    if (!modelItem) {
      continue;
    }
    if (
      (!modelItem.controlType && inputData.controlType) ||
      (modelItem.controlType !== inputData.controlType &&
        modelItem.controlType === 'dropdown' &&
        inputData.controlType === 'tags' &&
        inputData.column.multipleValues)
    ) {
      modelItem.controlType = inputData.controlType;
    }

    // NOTE the logic for `included` depends on `visible`, but can
    // have different results which should be reflected in the model
    if (inputData.included !== modelItem.visible) {
      modelItem.visible = inputData.included;
    }
  }
}

/**
 * Returns the controlType and dataType to be used to generate the form field for a column
 * @param column column for which the field control type is calculated
 * @param pregenerationElementsMap the map of elements that can be used for each data type
 * @param modelColumnControlType previously stored/selected control type in the model for the current column
 */
export function getFieldData(
  column: DatasetColumn,
  pregenerationElementsMap: Record<ColumnDataType, GeneratorInputOption[]>,
  modelColumnControlType: GenerationElementType | undefined,
): { controlType: GenerationElementType | undefined; dataType: ColumnDataType | undefined } {
  let dataType = column.dataType;
  let controlType = modelColumnControlType;

  // If initial run (control type not selected) and data type present - select the control type
  if (!controlType && dataType) {
    controlType = pregenerationElementsMap[dataType]?.[0]?.value;

    // If Link column and multiple values are used - use the default control for Tag column
    if (dataType === 'Link' && column.link?.multipleValues) {
      controlType = 'tags';
    } else if (column.isComputed) {
      controlType = 'textField';
    }
  }

  if (!controlType && column.isComputed) {
    // Fallback to the first available option for string
    controlType = pregenerationElementsMap.String[0].value;
    dataType = 'String';
  }

  if (
    column.columnType === 'CreatedTime' ||
    column.columnType === 'LastUpdatedTime' ||
    column.format?.formatType === 'TimeZone'
  ) {
    controlType = 'textField';
  }

  if (column.isInline && !controlType) {
    controlType = 'textField';
  }

  if (controlType === 'dropdown' && column.multipleValues) {
    controlType = 'tags';
  }

  return { controlType, dataType };
}

export function prepareUpdatePropsForInput(
  inputData: PregenerationData,
  sameDataSourceDifferentView: boolean,
  existingElement: CanvasElement | undefined,
): any {
  let updateProps: any = {};

  switch (inputData.controlType) {
    case 'audioRecorder':
      updateProps.content = {
        ...updateProps?.content,
        options: {
          ...updateProps?.content?.options,
        },
      };
      break;

    case 'dateInput':
      updateProps.content = {
        ...updateProps.content,
        time: { ...updateProps.content?.time, enabled: false },
        date: {
          ...updateProps.content?.date,
          defaultValue: inputData.column.defaultValue === DATE_DEFAULT_VALUE ? 'today' : 'none',
          enabled: true,
        },
      };
      if (existingElement && hasProps(existingElement)) {
        const dateInputProps = existingElement.props as DatetimeInputPropsModel;
        if (!dateInputProps.content?.date?.enabled || dateInputProps.content?.time?.enabled) {
          updateProps.dirty = {
            ...updateProps.dirty,
            displayVariant: true,
          };
        }
      }
      break;

    case 'datetimeInput':
      updateProps.content = {
        ...updateProps.content,
        time: { ...updateProps.content?.time, enabled: true },
        date: {
          ...updateProps.content?.date,
          defaultValue: inputData.column.defaultValue === DATETIME_DEFAULT_VALUE ? 'today' : 'none',
          enabled: true,
        },
      };
      if (existingElement && hasProps(existingElement)) {
        const dateInputProps = existingElement.props as DatetimeInputPropsModel;
        if (!dateInputProps.content?.date?.enabled || !dateInputProps.content?.time?.enabled) {
          updateProps.dirty = {
            ...updateProps.dirty,
            displayVariant: true,
          };
        }
      }
      break;

    case 'signature':
      return applyPropsPatch<'signature'>(updateProps, {
        content: { input: { signatureType: inputData.column.signatureFormat?.signatureType } },
      });

    case 'textInput':
    case 'textField':
      updateProps = updateGeneratedTextInputContent(inputData, updateProps, existingElement);
      updateProps.dirty = {
        // cleaned up after merging
        format: undefined,
        precision: undefined,
        currency: undefined,
        duration: undefined,
      };
      break;

    case 'timeInput':
      updateProps.content = {
        ...updateProps.content,
        time: {
          ...updateProps.content?.time,
          defaultTime: inputData.column.defaultValue,
          enabled: true,
        },
        date: { ...updateProps.content?.date, enabled: false },
      };
      break;

    case 'uploadFile':
      updateProps.content = {
        ...updateProps?.content,
        options: {
          ...updateProps?.content?.options,
          multiple: inputData.column.multipleValues || false,
        },
      };
      break;

    case 'tags':
      updateProps = applyPropsPatch<'tags'>(updateProps, {
        content: {
          options: {
            ...(sameDataSourceDifferentView ? { readonly: inputData.readonly } : undefined),
            singleSelect: !inputData.column.multipleValues,
          },
        },
      });
      break;

    case 'checkboxInput':
      if (sameDataSourceDifferentView) {
        updateProps = applyPropsPatch<'checkboxInput'>(updateProps, {
          content: { input: { readonly: inputData.readonly } },
        });
      }
      break;
  }

  return updateProps;
}

/**
 * Finds the slot in which the field should be placed.
 *
 * It starts from the last slot and then looks for last non-empty column with enough space.
 */
export function findSlotForField(
  entities: Dictionary<CanvasElement>,
  element: CanvasElementFrom<'container' | 'formContainer' | 'formDetails' | 'editableList'>,
  maxFieldsPerColumn: number,
  checkAllSlots?: boolean,
): SlotIdOf<'container' | 'formContainer' | 'formDetails' | 'editableList'> {
  const slotIds = element.slots.ids;

  // Use the last column as the initial choice
  let lastOkSlotId = slotIds[slotIds.length - 1];

  // Start from the second to last column
  for (let i = slotIds.length - 2; i >= 0; i--) {
    const slotId = slotIds[i];
    const children = element.slots[slotId]?.children || [];
    // Don't treat break lines as elements
    const childrenWithoutBreaklines = children.filter(
      (childId) => entities[childId]?.type !== 'breakLine',
    );
    const childrenCount = childrenWithoutBreaklines.length;

    if (childrenCount === 0) {
      // If column empty - set this as the current choice and continue to check the previous slot
      lastOkSlotId = slotId;
      continue;
    } else if (childrenCount < maxFieldsPerColumn) {
      // If column not empty, but has enough space - set this as the final choice and stop
      lastOkSlotId = slotId;
      break;
    } else if (!checkAllSlots) {
      // If column already full - use whatever was stored previously/initially and stop
      break;
    }
  }

  return lastOkSlotId;
}

/**
 * Returns the column container if it's present
 * @param entities the dictionary which should include all elements in the form's namespace
 * @param parentElement the reference to the form/list element
 * @returns the column container element or undefined if it is missing
 */
export function getColumnContainer(
  entities: Dictionary<CanvasElement>,
  parentElement: CanvasElementFrom<
    'formContainer' | 'formDetails' | 'editableList' | 'formDetailsList'
  >,
): CanvasElementFrom<'container'> | undefined {
  if (!hasWrapperRefs(parentElement)) {
    return undefined;
  }

  const columnContainerId = parentElement.props.wrapper.refs[COLUMN_CONTAINER_REFID];
  const columnContainer = entities[columnContainerId];

  return isElementType(columnContainer, 'container') ? columnContainer : undefined;
}

export function getLastGeneratedElementPath(
  entities: Dictionary<CanvasElement>,
  form: CanvasElementFrom<'formContainer' | 'formDetails' | 'editableList' | 'formDetailsList'>,
  targetSlot: SlotsFor<
    CanvasElementFrom<
      'formContainer' | 'formDetails' | 'container' | 'editableList' | 'formDetailsList'
    >
  >[ColumnId],
): ElementTargetPath | undefined {
  // If no refs - no last generated element
  if (!hasWrapperRefs(form)) {
    return;
  }

  const refs = getWrapperRefs(form);
  // Find all generated fields that are placed inside of the form
  const generatedElements = Object.entries(refs)
    .filter(
      ([refId, eId]) => WrapperField.isRefId(refId) && isElementChildOf(entities, eId, form.id),
    )
    .map(([, eId]) => eId);

  // Go through the slot
  if (!targetSlot) {
    throw new Error('Form slot error');
  }

  const lastFieldId = targetSlot.children.reduce(
    (lastId, childId) => {
      if (generatedElements.includes(childId)) {
        lastId = childId;
      }
      return lastId;
    },
    undefined as CanvasElementId | undefined,
  );

  return getElementPath(entities, lastFieldId);
}

export function buildFormInputValueBinding(
  formId: CanvasElementId,
  column: DataBindingSourceInfoColumn,
  canvasType: CanvasElementType,
  sourceType: DataBindingSourceType = 'formContainer',
): CanvasElementDataProperties {
  const key = getFormInputNameByType(canvasType);
  const columnKey = getColumnKey(column);

  return {
    enabled: true,
    key,
    dataSource: { id: formId, sourceType, name: columnKey, columns: [column] },
    bindings: { [key]: columnKey },
  };
}

/**
 * A control-type change replaces the element's props with the new type's initial props
 * (transformElementTypeMutably), losing a user-set placeholder. Both types declare where
 * their placeholder lives (metaUiFeatures.paths.placeholder), so the old value can ride
 * updateProps onto the new type's path. Same-type regenerations merge props in place and
 * never lose the placeholder - no patch needed.
 */
export function placeholderCarryOverPatch(
  existingElement: CanvasElement | undefined,
  targetType: CanvasElementType,
  getElementDefinition: (
    elementType: CanvasElementType,
  ) => CanvasElementDefinitionInternal | undefined,
): Record<string, unknown> | undefined {
  if (!existingElement || existingElement.type === targetType || !hasProps(existingElement)) {
    return undefined;
  }
  const sourcePath = getElementDefinition(existingElement.type)?.metaUiFeatures?.paths?.placeholder;
  const targetPath = getElementDefinition(targetType)?.metaUiFeatures?.paths?.placeholder;
  const value = sourcePath ? getValueByPath<string>(existingElement.props, sourcePath) : undefined;
  if (!value || !targetPath) {
    return undefined;
  }
  const patch = {};
  setValueByPath(patch, targetPath, value);
  return patch;
}

export function prepareFieldElements(
  formId: CanvasElementId,
  pregenerationData: PregenerationData[],
  inputDataSourceId: string,
  sourceType: 'formContainer' | 'editableList',
  sameDataSourceDifferentView = false,
  getSchema: (tableId: string) => BlazeTableSchema | undefined,
  getElementDefinition: (
    elementType: CanvasElementType,
  ) => CanvasElementDefinitionInternal | undefined,
  initializeGeneratedChildProps: InitializeGeneratedChildPropsFn,
  entityState: EntityState<CanvasElement>,
  formRefs: WrapperRefs,
  styleSheets: StyleSheet[],
): CreateCanvasElementModel[] {
  const fieldElements: CreateCanvasElementModel[] = [];

  for (const inputData of pregenerationData) {
    if (!inputData.included) {
      continue;
    }
    if (!inputData.controlType) {
      console.warn('Control type missing', inputData);
      continue;
    }

    const controlType = mapGenerationToRegularElementType(inputData);

    const bindings: CanvasElementDataProperties[] = [
      buildFormInputValueBinding(formId, inputData.column, controlType, sourceType),
    ];

    // Prepare element definition and props
    const elementDefinition = getElementDefinition(controlType);
    if (!elementDefinition) {
      console.warn(`Could not find element definition for type: ${controlType}`);
      continue;
    }

    if (elementDefinition.ioConfig?.inputs?.find((i) => i.name === 'optionsList')) {
      const optionsBinding = getOptionsListBinding(
        inputData,
        inputDataSourceId,
        getSchema,
        pregenerationData,
      );
      if (optionsBinding) {
        bindings.push(optionsBinding);
      }
    }

    if (sourceType === 'formContainer') {
      elementDefinition.width = { ...elementDefinition.width, mode: 'full' };
    }

    // Prepare initial props (applied at creation time, which can be customized later on by the user)
    const baseProps = applyPropsPatch(deepClone(elementDefinition.initial), {
      content: { title: { label: inputData.title } } as any,
      data: bindings,
    });
    const initProps =
      initializeGeneratedChildProps(
        controlType,
        {
          props: baseProps,
          inputData,
        },
        styleSheets,
      ) ?? baseProps;

    const elementId: CanvasElementId | undefined = findByRefIds(formRefs, inputData.refIds);
    const existingElement = elementId ? entityState.entities[elementId] : undefined;
    // Prepare update props - which should always be updated with each regeneration
    let updateProps = prepareUpdatePropsForInput(
      inputData,
      sameDataSourceDifferentView,
      existingElement,
    );
    const placeholderPatch = placeholderCarryOverPatch(
      existingElement,
      controlType,
      getElementDefinition,
    );
    if (placeholderPatch) {
      updateProps = applyPropsPatch(updateProps, placeholderPatch);
    }

    fieldElements.push({
      inputData,
      refIds: inputData.refIds,
      elementDefinition,
      initProps,
      updateProps,
    });
  }

  return fieldElements;
}

function getOptionsListBinding(
  inputData: PregenerationData,
  inputDataSourceId: string,
  getSchema: (tableId: string) => BlazeTableSchema | undefined,
  pregenerationData: PregenerationData[],
): CanvasElementDataProperties | undefined {
  if (inputData.column.format?.formatType === 'TimeZone') {
    return {
      key: 'optionsList',
      bindings: { label: 'label', value: 'value' },
      enabled: true,
      dataSource: {
        sourceType: 'api-call',
        id: GET_TIMEZONES_API_ID,
        columns: [],
      },
    };
  }
  if (inputData.column.dataType === 'Link' && inputData.column.link?.tableId) {
    const link = inputData.column.link;
    /**
     * Note: this schema should be obtained before running form regeneration, so the schema is available here synchronously.
     * {@link FormGenerationService.prepareFormGenerationDataAndPreloadSchemas}
     */
    const sourceSchema = getSchema(link.tableId);
    if (!sourceSchema) {
      return;
    }
    const linkValueColumnKey = sourceSchema?.isBlazeTable
      ? findIdColumnKey(sourceSchema)
      : link.columnName;
    const linkDisplayColumnKey = sourceSchema?.isBlazeTable
      ? findDisplayColumnOrGetSingle(sourceSchema)
      : link.displayColumn;

    const valueColumn =
      findColumnKey(sourceSchema.columns, linkValueColumnKey) ||
      findIdColumnKey(sourceSchema) ||
      '';
    const labelColumn =
      findColumnKey(sourceSchema.columns, linkDisplayColumnKey) ||
      findDisplayColumnOrGetSingle(sourceSchema) ||
      '';

    return {
      enabled: true,
      key: 'optionsList',
      dataSource: { ...sourceSchema, sourceType: 'dataset', projectColumns: true },
      bindings: {
        value: valueColumn,
        label: labelColumn,
      },
    };
  }
  if (isLinkedInlineColumn(inputData.column)) {
    const linkData = getLinkedInlineColumns(inputData.column.formula);
    if (!linkData?.inlineColumnId || !linkData?.linkColumnId) {
      return;
    }

    const link = findColumn(
      pregenerationData.map((c) => c.column),
      linkData?.linkColumnId,
    )?.link;
    if (!link) {
      return;
    }

    const sourceSchema = getSchema(link.tableId);
    const inlineColumn = findColumn(sourceSchema?.columns, linkData?.inlineColumnId);
    if (inlineColumn?.columnType === 'Tag') {
      return getOptionsListBindingForTagColumn(link.tableId, getColumnKey(inlineColumn));
    }
  }
  if (inputData.column.dataType === 'Tag') {
    return getOptionsListBindingForTagColumn(inputDataSourceId, getColumnKey(inputData.column));
  }
  return undefined;
}

export function getNewFormFieldVisibility(
  col: DatasetColumn,
  accountsTableName?: string,
): false | undefined {
  if (col.dataType === 'Link' && !!accountsTableName && col.link?.tableName === accountsTableName) {
    return false;
  }
  return undefined;
}

export function columnsToPregenerationData(
  columns: DataBindingSourceInfoColumn[],
  model: (FormContainerModelField | FormListModelField)[] | undefined,
  options: {
    isFormReadonly: boolean;
    isInsertForm: boolean;
    includedLimit?: number;
    overrideColumnVisibility?: Record<ColumnId | string, boolean>;
    fieldsLabelGen?: number;
  },
): PregenerationData[] {
  const { isFormReadonly, includedLimit, overrideColumnVisibility, fieldsLabelGen } = options;
  const pregenerationElementsMap = getFormFieldsDataTypeMapping(isFormReadonly);
  let includedCount = 0;

  return columns.map((column) => {
    const modelColumn = model?.find(matchCustomColumn(column, 'fieldId', 'field'));

    const { controlType, dataType } = getFieldData(
      column,
      pregenerationElementsMap,
      modelColumn?.controlType as GenerationElementType | undefined,
    );

    // is set as invisible in the model
    const isInvisible = !!modelColumn && modelColumn.visible === false;
    const hiddenByDefault =
      modelColumn?.visible !== true &&
      !isFormReadonly &&
      column.columnType &&
      (ELEMENTS_HIDDEN_BY_DEFAULT_ON_INSERT_FORM.includes(column.columnType) || column.isInline);
    let included = !column.isInternal && !!controlType && !isInvisible && !hiddenByDefault;

    if (overrideColumnVisibility?.[getColumnKey(column)] !== undefined) {
      included = overrideColumnVisibility[getColumnKey(column)];
    }

    if (included && includedLimit) {
      includedCount++;
      if (includedCount > includedLimit) {
        included = false;
      }
    }

    return {
      id: column.id,
      name: column.name,
      refIds: WrapperField.getRefIds(column.name, column.id),
      included,
      column: { ...column, dataType },
      controlType,
      format: getFormatByDataType(dataType, column.format?.formatType),
      readonly: column.isComputed || isFormReadonly,
      title: isFormListModelField(modelColumn)
        ? modelColumn.title
        : toResolvedColumnName(column.name, fieldsLabelGen),
    };
  });
}

export function mapGenerationToRegularElementType(
  control: GenerationElementType | PregenerationData,
): CanvasElementType {
  if (
    typeof control !== 'string' &&
    !control.readonly &&
    control.column.format?.formatType === 'TimeZone'
  ) {
    return 'dropdown';
  }

  const controlType: GenerationElementType | undefined =
    typeof control === 'string' ? control : control.controlType;

  switch (controlType) {
    case 'dateInput':
    case 'timeInput':
      return 'datetimeInput';
    case undefined:
      throw new Error('Unexpected empty control type');
    default:
      return controlType;
  }
}
